
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Loader2, RefreshCw, ArrowUpDown, Settings, Check, FilterX, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// --- CONFIGURATIONS ---
const CLIENT_COLUMNS = [
    { key: 'client', label: 'Client', width: 300 },
    { key: 'sales_p1', label: 'Valoare Vanduta 2024', width: 150 },
    { key: 'sales_p2', label: 'Valoare Vanduta 2025', width: 150 },
    { key: 'profit_p1', label: 'Profit 2024', width: 140 },
    { key: 'profit_p2', label: 'Profit 2025', width: 140 },
    { key: 'profit_pct_p1', label: 'Profit % 2024', width: 120 },
    { key: 'profit_pct_p2', label: 'Profit % 2025', width: 120 },
];

const ARTICLE_COLUMNS = [
    { key: 'code', label: 'Cod Articol', width: 150 },
    { key: 'name', label: 'Articol', width: 350 },
    { key: 'qty_p1', label: 'Cantitate 2024', width: 120 },
    { key: 'qty_p2', label: 'Cantitate 2025', width: 120 },
    { key: 'sales_p1', label: 'Valoare Vanduta 2024', width: 150 },
    { key: 'sales_p2', label: 'Valoare Vanduta 2025', width: 150 },
    { key: 'profit_p1', label: 'Profit 2024', width: 140 },
    { key: 'profit_p2', label: 'Profit 2025', width: 140 },
    { key: 'profit_pct_p1', label: 'Profit % 2024', width: 120 },
    { key: 'profit_pct_p2', label: 'Profit % 2025', width: 120 },
];

export default function AvicarvilAnalysis() {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [articlesLoading, setArticlesLoading] = useState(false);
    const [table1, setTable1] = useState('');
    const [table2, setTable2] = useState('');

    // Data States
    const [clientData, setClientData] = useState(null);
    const [articleData, setArticleData] = useState(null);

    // Selection State
    const [selectedClient, setSelectedClient] = useState(null);

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${API}/tables`);
            setTables(res.data || []);
        } catch (e) {
            console.error(e);
            toast.error("Nu am putut incarca lista de fisiere.");
        }
    };

    const handleCompare = async () => {
        if (!table1 || !table2) {
            toast.warning("Te rog selecteaza ambele perioade.");
            return;
        }
        setLoading(true);
        setClientData(null);
        setArticleData(null);
        setSelectedClient(null); // Reset selection on new compare

        try {
            // Fetch Clients first
            const resClients = await axios.post(`${API}/reports/avicarvil-analysis`, { table1, table2 });

            if (resClients.data.error) toast.error(`Clienti: ${resClients.data.error}`);
            else setClientData(resClients.data);

            // Fetch All Articles initially
            await fetchArticles(null);

            toast.success("Date incarcate cu succes!");

        } catch (e) {
            console.error(e);
            toast.error("Eroare la incarcarea datelor.");
        } finally {
            setLoading(false);
        }
    };

    const fetchArticles = async (clientFilter = null) => {
        if (!table1 || !table2) return;
        setArticlesLoading(true);
        try {
            const res = await axios.post(`${API}/reports/avicarvil-articles-analysis`, {
                table1,
                table2,
                client_filter: clientFilter
            });

            if (res.data.error) {
                toast.error(`Articole: ${res.data.error}`);
                setArticleData([]);
            } else {
                setArticleData(res.data);
            }
        } catch (e) {
            console.error(e);
            toast.error("Eroare la incarcarea articolelor.");
        } finally {
            setArticlesLoading(false);
        }
    };

    const handleClientClick = (row) => {
        const clientName = row.client;
        if (selectedClient === clientName) {
            // Deselect if same clicked
            handleClearSelection();
        } else {
            setSelectedClient(clientName);
            fetchArticles(clientName);
            toast.info(`Filtrare articole pentru: ${clientName}`);
        }
    };

    const handleClearSelection = () => {
        setSelectedClient(null);
        fetchArticles(null);
    };

    const p1Label = getYearLabel(table1) || 'P1';
    const p2Label = getYearLabel(table2) || 'P2';

    return (
        <div className="p-8 space-y-8 pb-40">
            <div className="flex items-center space-x-4">
                <Link to="/reports">
                    <Button variant="ghost" className="text-gray-400 hover:text-white">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Inapoi la Rapoarte
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-3xl font-bold text-white">Analiza Clienti Avicarvil</h1>
                </div>
            </div>

            {/* Selection Card */}
            <Card className="bg-[#18181b] border-white/10">
                <CardHeader>
                    <CardTitle className="text-gray-300 text-lg">Selectie Perioade</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada 1 (Ex: 2024)</label>
                        <Select value={table1} onValueChange={setTable1}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white"><SelectValue placeholder="Selecteaza fisier..." /></SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada 2 (Ex: 2025)</label>
                        <Select value={table2} onValueChange={setTable2}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white"><SelectValue placeholder="Selecteaza fisier..." /></SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={handleCompare} disabled={loading || !table1 || !table2} className="bg-red-600 hover:bg-red-700 text-white">
                        {loading ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        Genereaza Raport
                    </Button>
                </CardContent>
            </Card>

            {/* TABEL CLIENTI */}
            {clientData && (
                <ExtendedTable
                    title="Raport Clienti"
                    data={clientData}
                    columnsConfig={CLIENT_COLUMNS}
                    p1Label={p1Label}
                    p2Label={p2Label}
                    onRowClick={handleClientClick}
                    selectedRowKey="client"
                    selectedRowValue={selectedClient}
                    extraHeaderContent={
                        selectedClient && (
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleClearSelection(); }} className="ml-4 border-yellow-500 text-yellow-500 hover:bg-yellow-500/10">
                                <X className="mr-2 h-3 w-3" />
                                Clear Filter: {selectedClient}
                            </Button>
                        )
                    }
                />
            )}

            {/* TABEL ARTICOLE */}
            {articleData && (
                <ExtendedTable
                    title={selectedClient ? `Raport Articole - ${selectedClient}` : "Raport Articole (Top Vanzari)"}
                    data={articleData}
                    columnsConfig={ARTICLE_COLUMNS}
                    p1Label={p1Label}
                    p2Label={p2Label}
                    isLoading={articlesLoading}
                />
            )}
        </div>
    );
}

// --- REUSABLE EXTENDED TABLE COMPONENT ---
function ExtendedTable({ title, data, columnsConfig, p1Label, p2Label, headerClassName, onRowClick, selectedRowKey, selectedRowValue, extraHeaderContent, isLoading }) {
    const [sortConfig, setSortConfig] = useState({ key: columnsConfig[1]?.key || 'value', direction: 'desc' });
    const [columnFilters, setColumnFilters] = useState({});
    const [columnWidths, setColumnWidths] = useState(
        columnsConfig.reduce((acc, col) => ({ ...acc, [col.key]: col.width }), {})
    );
    const [visibleColumns, setVisibleColumns] = useState(
        columnsConfig.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    );

    // --- PAGINATION STATE ---
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Reset page when filters/sort change
    useEffect(() => {
        setCurrentPage(1);
    }, [columnFilters, sortConfig, data]);

    // Filter & Sort Logic
    const formatMoney = (val) => {
        if (val === undefined || val === null) return '-';
        return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatPercent = (val) => {
        if (val === undefined || val === null) return '-';
        return `${(val * 100).toFixed(0)}%`;
    };

    const formatQty = (val) => {
        if (val === undefined || val === null) return '-';
        return Number(val).toLocaleString('ro-RO', { maximumFractionDigits: 0 });
    }

    const filteredData = useMemo(() => {
        if (!data) return [];
        let items = [...data];

        // 1. Column Filters
        const activeFilterKeys = Object.keys(columnFilters).filter(k => columnFilters[k]);
        if (activeFilterKeys.length > 0) {
            items = items.filter(item => {
                return activeFilterKeys.every(key => {
                    const filterVal = columnFilters[key].toLowerCase();
                    let rawVal = item[key];
                    let strVal = '';

                    if (key.includes('qty')) {
                        strVal = String(Math.round(rawVal || 0));
                    } else if (typeof rawVal === 'number') {
                        if (key.includes('pct')) {
                            strVal = `${(rawVal * 100).toFixed(0)}%`; // Match displayed percent
                        } else {
                            strVal = rawVal.toFixed(2); // Match displayed money approximately
                        }
                    } else {
                        strVal = String(rawVal || '');
                    }

                    return strVal.toLowerCase().includes(filterVal);
                });
            });
        }

        // 2. Sort
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [data, columnFilters, sortConfig]);

    // Derived Logic for Pagination
    const totalPages = Math.ceil(filteredData.length / pageSize);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage]);

    const handleNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));
    const handlePrev = () => setCurrentPage(p => Math.max(1, p - 1));

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleResizeStart = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = columnWidths[key];
        const handleMouseMove = (ev) => {
            const diff = ev.clientX - startX;
            setColumnWidths(prev => ({ ...prev, [key]: Math.max(50, startWidth + diff) }));
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
    };

    const toggleColumn = (key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    const clearFilters = () => setColumnFilters({});

    return (
        <Card className="bg-[#18181b] border-white/10 relative">
            {isLoading && (
                <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center rounded-lg backdrop-blur-sm">
                    <Loader2 className="animate-spin text-white h-8 w-8" />
                </div>
            )}
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-yellow-400 rounded-t-lg">
                <div className="flex items-center gap-4">
                    <h3 className="text-lg font-bold text-black mr-4">{title}</h3>
                    {extraHeaderContent}
                    <span className="text-black/80 text-sm border-l border-black/20 pl-4 ml-2 font-medium">
                        {filteredData.length} rezultate • Pagina {currentPage} din {Math.max(1, totalPages)}
                    </span>
                    {Object.keys(columnFilters).some(k => columnFilters[k]) && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700 hover:bg-red-100 h-8">
                            <FilterX className="mr-2 h-3 w-3" /> Reset Filtre
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrev}
                        disabled={currentPage === 1}
                        className="border-black/20 text-black hover:bg-black/10 disabled:opacity-50"
                    >
                        Anterior
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNext}
                        disabled={currentPage >= totalPages}
                        className="border-black/20 text-black hover:bg-black/10 disabled:opacity-50"
                    >
                        Urmator
                    </Button>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="border-black/20 text-black hover:bg-black/10">
                                <Settings className="mr-2 h-4 w-4" /> Coloane
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 bg-[#18181b] border-white/20 text-white p-0">
                            <div className="p-3 border-b border-white/10 bg-white/5"><h4 className="font-medium text-sm text-gray-400">Selecteaza Coloane</h4></div>
                            <div className="p-1 max-h-[300px] overflow-y-auto">
                                {columnsConfig.map(col => (
                                    <div key={col.key} onClick={() => toggleColumn(col.key)} className="flex items-center space-x-2 p-2 hover:bg-white/10 rounded cursor-pointer transition-colors">
                                        <div className={`shrink-0 w-4 h-4 border border-white/40 rounded flex items-center justify-center ${visibleColumns[col.key] ? 'bg-red-600 border-red-600' : ''}`}>
                                            {visibleColumns[col.key] && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                        <span className="text-sm select-none">{col.label}</span>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table style={{ tableLayout: 'fixed', minWidth: '100%' }}>
                    <TableHeader>
                        <TableRow className={`border-white/10 ${headerClassName || 'bg-[#27272a] hover:bg-[#27272a]'}`}>
                            {columnsConfig.map(col => visibleColumns[col.key] && (
                                <TableHead
                                    key={col.key}
                                    style={{ width: columnWidths[col.key], position: 'relative' }}
                                    className={`font-bold border-r border-white/10 select-none ${headerClassName ? 'text-black font-extrabold !bg-yellow-400' : 'text-gray-200'}`}
                                >
                                    <div className="flex items-center justify-between cursor-pointer py-2" onClick={() => handleSort(col.key)}>
                                        <span className="truncate" title={col.label}>
                                            {col.label.replace('2024', p1Label).replace('2025', p2Label)}
                                        </span>
                                        {sortConfig.key === col.key && <ArrowUpDown className="h-4 w-4 ml-1 opacity-50 shrink-0" />}
                                    </div>
                                    <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-black/20 transition-colors z-10" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                                </TableHead>
                            ))}
                        </TableRow>
                        <TableRow className={`border-white/10 ${headerClassName || 'bg-[#27272a] hover:bg-[#27272a]'}`}>
                            {columnsConfig.map(col => visibleColumns[col.key] && (
                                <TableHead key={`filter-${col.key}`} className={`p-2 border-r border-white/10 ${headerClassName ? '!bg-yellow-400' : ''}`}>
                                    <Input
                                        value={columnFilters[col.key] || ''}
                                        onChange={(e) => setColumnFilters(p => ({ ...p, [col.key]: e.target.value }))}
                                        placeholder="Filtru..."
                                        className={`h-8 text-xs border-white/20 focus:ring-red-500 ${headerClassName ? 'bg-white/80 text-black placeholder:text-gray-500' : 'bg-[#18181b] text-white'}`}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedData.map((row, idx) => {
                            const isSelected = selectedRowKey && row[selectedRowKey] === selectedRowValue;
                            return (
                                <TableRow
                                    key={idx}
                                    className={`border-white/10 cursor-pointer transition-colors ${isSelected ? 'bg-yellow-500/20 hover:bg-yellow-500/30' : 'hover:bg-white/5'}`}
                                    onClick={() => onRowClick && onRowClick(row)}
                                >
                                    {columnsConfig.map(col => visibleColumns[col.key] && (
                                        <TableCell key={col.key} className="border-r border-white/10 truncate font-mono text-white">
                                            {/* Custom Formatting Logic */}
                                            {(() => {
                                                const val = row[col.key];
                                                if (col.key === 'client' || col.key === 'name' || col.key === 'code') return <span className="font-sans font-bold">{val}</span>;
                                                if (col.key.includes('qty')) return <span className="text-right block text-gray-300">{formatQty(val)}</span>;
                                                if (col.key.includes('sales') || col.key.includes('profit') && !col.key.includes('pct')) return <span className="text-right block">{formatMoney(val)}</span>;
                                                if (col.key.includes('pct')) {
                                                    const percent = Math.min(100, Math.max(0, (val || 0) * 100));
                                                    return (
                                                        <div className="relative w-full h-7 bg-black rounded-sm overflow-hidden border border-white/10">
                                                            <div
                                                                className="absolute top-0 left-0 h-full bg-yellow-400 transition-all duration-500 ease-out"
                                                                style={{ width: `${percent}%` }}
                                                            />
                                                            <span className="absolute inset-0 flex items-center justify-end px-2 text-xs font-bold text-white mix-blend-difference z-10">
                                                                {formatPercent(val)}
                                                            </span>
                                                        </div>
                                                    );
                                                }
                                                return val;
                                            })()}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {filteredData.length === 0 && (
                            <TableRow><TableCell colSpan={columnsConfig.length} className="text-center text-gray-500 py-8">Nu sunt date.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </Card>
    );
}

// Helpers
const getYearLabel = (tableName) => {
    if (!tableName) return '';
    const parts = tableName.split(/[_/]/);
    const year = parts.find(p => /^\d{4}$/.test(p));
    return year || tableName;
};
